import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import {
  Trophy,
  ChevronRight,
  Plus,
  Loader2,
  AlertCircle,
  ArrowLeft,
  BookOpen,
  Users,
  Briefcase,
  Pencil,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { getApiError } from '@/lib/utils';

const ROLE_LABELS = { auditor: 'Auditor', leadership: 'Leadership' };
const ROLE_COLORS = {
  auditor:    'bg-blue-100 text-blue-800 border-blue-200',
  leadership: 'bg-purple-100 text-purple-800 border-purple-200',
};

function RoleBadge({ role }) {
  return (
    <Badge className={`text-xs gap-1 ${ROLE_COLORS[role] || 'bg-slate-100 text-slate-700'}`}>
      {role === 'auditor' ? <Users className="h-3 w-3" /> : <Briefcase className="h-3 w-3" />}
      {ROLE_LABELS[role] || role}
    </Badge>
  );
}

function ActiveBadge({ active }) {
  return active ? (
    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1 text-xs">
      <CheckCircle2 className="h-3 w-3" /> Active
    </Badge>
  ) : (
    <Badge variant="outline" className="text-slate-500 gap-1 text-xs">
      <XCircle className="h-3 w-3" /> Inactive
    </Badge>
  );
}

export default function IncentiveSchemesPage() {
  const navigate = useNavigate();
  const { isAdmin, isSeniorScheduler } = useAuth();
  const { toast } = useToast();
  const canEdit = isAdmin || isSeniorScheduler;

  const [schemes, setSchemes] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sRes, tRes] = await Promise.all([
        api.get('/incentives/schemes', { params: { include_inactive: true } }),
        api.get('/incentives/types', { params: { include_inactive: true } }),
      ]);
      setSchemes(sRes.data);
      setTypes(tRes.data);
    } catch (err) {
      const msg = getApiError(err, 'Failed to load schemes.');
      setError(msg);
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Group schemes by incentive_type_id
  const byType = {};
  schemes.forEach(s => {
    if (!byType[s.incentive_type_id]) byType[s.incentive_type_id] = [];
    byType[s.incentive_type_id].push(s);
  });

  const typeMap = Object.fromEntries(types.map(t => [t.id, t]));

  return (
    <div className="space-y-6" data-testid="incentive-schemes-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/incentives" className="hover:text-slate-700 flex items-center gap-1">
          <Trophy className="h-4 w-4" /> Incentives
        </Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-slate-700 font-medium">Incentive Rules</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/incentives')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-slate-500" />
              <h1 className="text-xl font-semibold text-slate-900">Incentive Rules</h1>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              Configure rules, thresholds and weightings per incentive type and role
            </p>
          </div>
        </div>
        {canEdit && (
          <Button onClick={() => navigate('/incentives/schemes/new')} className="gap-2" data-testid="new-scheme-btn">
            <Plus className="h-4 w-4" /> New Scheme
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <AlertCircle className="h-10 w-10 text-red-400" />
            <p className="text-sm text-slate-600">{error}</p>
            <Button variant="outline" size="sm" onClick={fetchData}>Retry</Button>
          </CardContent>
        </Card>
      ) : schemes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-20 text-center">
            <div className="flex items-center justify-center h-16 w-16 rounded-full bg-slate-100">
              <BookOpen className="h-8 w-8 text-slate-400" />
            </div>
            <div>
              <p className="font-medium text-slate-700">No incentive schemes yet</p>
              <p className="text-sm text-slate-500 mt-1">
                Create a scheme to define rules and thresholds for each incentive type and role.
              </p>
            </div>
            {canEdit && (
              <Button onClick={() => navigate('/incentives/schemes/new')} className="gap-2" data-testid="empty-new-scheme-btn">
                <Plus className="h-4 w-4" /> New Scheme
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(byType).map(([typeId, typeSchemes]) => {
            const typeMeta = typeMap[Number(typeId)];
            return (
              <div key={typeId}>
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-amber-500" />
                  {typeMeta?.name || `Type #${typeId}`}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {typeSchemes.map(scheme => (
                    <Card
                      key={scheme.id}
                      className={`cursor-pointer hover:border-slate-300 hover:shadow-sm transition-all ${!scheme.is_active ? 'opacity-60' : ''}`}
                      onClick={() => navigate(`/incentives/schemes/${scheme.id}`)}
                      data-testid={`scheme-card-${scheme.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="space-y-2 min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <RoleBadge role={scheme.role_type} />
                              <ActiveBadge active={scheme.is_active} />
                            </div>

                            {/* Summary stats */}
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600 mt-2">
                              {scheme.period_cutoff_day != null && (
                                <span>Cut-off: day {scheme.period_cutoff_day}</span>
                              )}
                              {scheme.revenue_threshold != null && (
                                <span>Revenue: £{Number(scheme.revenue_threshold).toLocaleString()}</span>
                              )}
                              {scheme.on_time_threshold_pct != null && (
                                <span>On-time: {scheme.on_time_threshold_pct}%</span>
                              )}
                              <span>{scheme.criteria?.length ?? 0} criteria</span>
                              {(scheme.overrides?.length ?? 0) > 0 && (
                                <span>{scheme.overrides.length} override{scheme.overrides.length !== 1 ? 's' : ''}</span>
                              )}
                            </div>

                            {scheme.criteria?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {scheme.criteria.map(c => (
                                  <span key={c.key} className="text-xs bg-slate-100 rounded px-1.5 py-0.5 text-slate-600">
                                    {c.label} {c.weight_pct}%
                                  </span>
                                ))}
                              </div>
                            )}

                            <p className="text-xs text-slate-400 mt-1">
                              {scheme.updated_at
                                ? `Updated ${new Date(scheme.updated_at).toLocaleDateString('en-GB')} by ${scheme.updated_by_name || '—'}`
                                : `Created by ${scheme.created_by_name || '—'}`}
                            </p>
                          </div>
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 flex-shrink-0"
                              onClick={e => { e.stopPropagation(); navigate(`/incentives/schemes/${scheme.id}`); }}
                              data-testid={`edit-scheme-${scheme.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
